import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PRIORITY_MAP, type AgencyTaskView } from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { date } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, PageHead } from '../../components/ui';
import { Avatar } from '../../components/People';
import { useToast } from '../../components/Toast';
import { TaskDrawer } from './TaskDrawer';

/**
 * مهامّي — عمل اليوم عبر كل المشاريع.
 *
 * كنسق «My Tasks» في أسانا: المتأخر أولًا ثم اليوم ثم القادم ثم ما بلا
 * موعد. والتجميعُ يُبدَّل لأن السؤال يتغيّر: «ماذا أنجز اليوم؟» صباحًا،
 * و«ما بقي في مشروع فلان؟» حين يسأل عميل، و«ما العاجل؟» حين يضيق الوقت.
 *
 * والأقسامُ تُطوى وتُفتح ويُحفظ ذلك: من يعمل على القادم لا يريد المتأخّر
 * مفتوحًا أمامه كلَّ صباح، ومن أغلق قسمًا يجده مغلقًا غدًا.
 */

type GroupBy = 'due' | 'project' | 'priority';
type Bucket = { key: string; title: string; hint?: string; tone?: string; rows: AgencyTaskView[] };

const GROUPS: { key: GroupBy; label: string; icon: 'calendar' | 'briefcase' | 'zap' }[] = [
  { key: 'due', label: 'بالاستحقاق', icon: 'calendar' },
  { key: 'project', label: 'بالمشروع', icon: 'briefcase' },
  { key: 'priority', label: 'بالأولوية', icon: 'zap' },
];

export function MyAgencyTasks() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [openTask, setOpenTask] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupBy>('due');
  const [projectFilter, setProjectFilter] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('goyone.mytasks.collapsed') ?? '[]'));
    } catch {
      return new Set();
    }
  });

  /* رابط ?task= يفتح البطاقة مباشرة — وجهة إشعار الإسناد. */
  useEffect(() => {
    const t = params.get('task');
    if (t) setOpenTask(t);
  }, [params]);

  const toggleSection = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem('goyone.mytasks.collapsed', JSON.stringify([...next]));
      } catch {
        /* التخزين قد يكون محجوبًا — الطيُّ زينةٌ لا تُعطّل الصفحة. */
      }
      return next;
    });
  };

  const tasks = useQuery({
    queryKey: ['agency', 'my-tasks', showDone],
    queryFn: () =>
      api<AgencyTaskView[]>(
        `/agency/tasks${qs({ mine: true, completed: showDone ? undefined : false })}`,
      ),
  });

  const toggle = useMutation({
    mutationFn: ({ taskId, completed }: { taskId: string; completed: boolean }) =>
      api(`/agency/tasks/${taskId}`, { method: 'PATCH', body: { completed } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['agency'] }),
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const all = tasks.data ?? [];

  /* مشاريعُ لها مهامٌّ عليّ — شرائطُ تصفيةٍ تُنقر. */
  const projectChips = useMemo(() => {
    const map = new Map<string, { id: string; name: string; color: string; count: number }>();
    for (const t of all) {
      if (t.completed || !t.projectName) continue;
      const cur = map.get(t.projectId) ?? {
        id: t.projectId,
        name: t.projectName,
        color: t.projectColor ?? '#6F7784',
        count: 0,
      };
      cur.count += 1;
      map.set(t.projectId, cur);
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [all]);

  const visible = useMemo(
    () => (projectFilter ? all.filter((t) => t.projectId === projectFilter) : all),
    [all, projectFilter],
  );

  const counts = useMemo(() => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = startOfDay.getTime() + 86_400_000;
    let open = 0;
    let late = 0;
    let today = 0;
    let doneWeek = 0;
    const weekAgo = startOfDay.getTime() - 7 * 86_400_000;
    for (const t of all) {
      if (t.completed) {
        if (t.completedAt && new Date(t.completedAt).getTime() >= weekAgo) doneWeek += 1;
        continue;
      }
      open += 1;
      if (!t.dueAt) continue;
      const due = new Date(t.dueAt).getTime();
      if (due < startOfDay.getTime()) late += 1;
      else if (due < endOfDay) today += 1;
    }
    return { open, late, today, doneWeek };
  }, [all]);

  const buckets = useMemo<Bucket[]>(() => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = startOfDay.getTime() + 86_400_000;
    const endOfWeek = startOfDay.getTime() + 7 * 86_400_000;

    const done = visible.filter((t) => t.completed);
    const open = visible.filter((t) => !t.completed);

    if (groupBy === 'project') {
      const map = new Map<string, Bucket>();
      for (const t of open) {
        const key = t.projectId;
        const b = map.get(key) ?? {
          key,
          title: t.projectName ?? 'بلا مشروع',
          rows: [],
        };
        b.rows.push(t);
        map.set(key, b);
      }
      const out = [...map.values()].sort((a, b) => b.rows.length - a.rows.length);
      if (showDone && done.length) out.push({ key: 'done', title: 'مكتملة', rows: done });
      return out;
    }

    if (groupBy === 'priority') {
      const order = ['HIGH', 'NORMAL', 'LOW'] as const;
      const out: Bucket[] = order.map((p) => ({
        key: p,
        title: PRIORITY_MAP[p].label,
        tone: p === 'HIGH' ? 'late' : undefined,
        rows: open.filter((t) => t.priority === p),
      }));
      if (showDone && done.length) out.push({ key: 'done', title: 'مكتملة', rows: done });
      return out.filter((b) => b.rows.length > 0);
    }

    const overdue: AgencyTaskView[] = [];
    const today: AgencyTaskView[] = [];
    const week: AgencyTaskView[] = [];
    const later: AgencyTaskView[] = [];
    const someday: AgencyTaskView[] = [];

    for (const t of open) {
      if (!t.dueAt) {
        someday.push(t);
        continue;
      }
      const due = new Date(t.dueAt).getTime();
      if (due < startOfDay.getTime()) overdue.push(t);
      else if (due < endOfDay) today.push(t);
      else if (due < endOfWeek) week.push(t);
      else later.push(t);
    }

    const out: Bucket[] = [
      { key: 'overdue', title: 'متأخرة', hint: 'فات موعدها ولم تكتمل', tone: 'late', rows: overdue },
      { key: 'today', title: 'اليوم', tone: 'now', rows: today },
      { key: 'week', title: 'هذا الأسبوع', rows: week },
      { key: 'later', title: 'لاحقًا', rows: later },
      {
        key: 'someday',
        title: 'بلا موعد',
        hint: 'عملٌ لن يذكّر به أحد — ضع له موعدًا',
        rows: someday,
      },
    ];
    if (showDone && done.length) out.push({ key: 'done', title: 'مكتملة', rows: done });
    return out.filter((b) => b.rows.length > 0);
  }, [visible, groupBy, showDone]);

  return (
    <div className="page">
      <PageHead
        title="مهامي"
        subtitle={
          counts.open
            ? `${counts.open} مهمّة مفتوحة عليك عبر كل المشاريع`
            : 'لا مهام مفتوحة عليك'
        }
        actions={
          <>
            <span className="seg" role="group" aria-label="التجميع">
              {GROUPS.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  className={`seg-btn${groupBy === g.key ? ' is-on' : ''}`}
                  onClick={() => setGroupBy(g.key)}
                  title={`تجميع ${g.label}`}
                >
                  <Icon name={g.icon} size={13} />
                  {g.label}
                </button>
              ))}
            </span>
            <label className="row tiny mute" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={showDone}
                onChange={(e) => setShowDone(e.target.checked)}
              />
              إظهار المكتملة
            </label>
          </>
        }
      />

      {tasks.isError && <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} />}
      {tasks.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {tasks.data && (
        <>
          <div className="ag-stats">
            <span className={`ag-stat${counts.late > 0 ? ' is-alert' : ''}`}>
              <Icon name="alert" size={18} />
              <strong className="num">{counts.late}</strong>
              <span>متأخّرة</span>
            </span>
            <span className="ag-stat">
              <Icon name="clock" size={18} />
              <strong className="num">{counts.today}</strong>
              <span>تستحق اليوم</span>
            </span>
            <span className="ag-stat">
              <Icon name="check" size={18} />
              <strong className="num">{counts.open}</strong>
              <span>مفتوحة</span>
            </span>
            <span className="ag-stat">
              <Icon name="zap" size={18} />
              <strong className="num">{counts.doneWeek}</strong>
              <span>أنجزتَها هذا الأسبوع</span>
            </span>
          </div>

          {projectChips.length > 1 && (
            <div className="ag-chip-bar">
              <button
                className={`ag-fchip${projectFilter === null ? ' is-on' : ''}`}
                onClick={() => setProjectFilter(null)}
              >
                الكل
                <b className="num">{counts.open}</b>
              </button>
              {projectChips.map((p) => (
                <button
                  key={p.id}
                  className={`ag-fchip${projectFilter === p.id ? ' is-on' : ''}`}
                  onClick={() => setProjectFilter(projectFilter === p.id ? null : p.id)}
                >
                  <span className="ag-dot" style={{ background: p.color }} />
                  {p.name}
                  <b className="num">{p.count}</b>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {tasks.data && counts.open === 0 && !showDone && (
        <EmptyState
          title="لا مهام عليك الآن"
          text="ما يُسنَد إليك من مهام المشاريع يظهر هنا مرتَّبًا باستحقاقه."
          action={
            <Link to="/agency/projects" className="btn btn-primary">
              تصفّح المشاريع
            </Link>
          }
        />
      )}

      {buckets.map((b) => {
        const isOpen = !collapsed.has(b.key);
        return (
          <section key={b.key} className={`ag-bucket${b.tone ? ` is-${b.tone}` : ''}`}>
            <button className="ag-bucket-head" onClick={() => toggleSection(b.key)}>
              <Icon name={isOpen ? 'chevronDown' : 'chevronRight'} size={13} />
              <h3 className={`ag-bucket-title${b.tone === 'late' ? ' is-late' : ''}`}>
                {b.title}
              </h3>
              <span className="num tiny mute">({b.rows.length})</span>
              {b.hint && <span className="tiny mute ag-bucket-hint">{b.hint}</span>}
            </button>

            {isOpen && (
              <ul className="ag-task-list">
                {b.rows.map((t) => (
                  <li key={t.id}>
                    <div className="ag-task-row" onClick={() => setOpenTask(t.id)}>
                      <button
                        className={`ag-check ag-check-sm${t.completed ? ' is-done' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle.mutate({ taskId: t.id, completed: !t.completed });
                        }}
                        aria-label={t.completed ? 'إعادة فتح' : 'إكمال'}
                      >
                        <Icon name="check" size={11} />
                      </button>

                      {t.isMilestone && (
                        <Icon name="diamond" size={13} className="ag-milestone-ic" />
                      )}

                      <span className={`ag-task-title${t.completed ? ' ag-done-text' : ''}`}>
                        {t.title}
                      </span>

                      {t.priority === 'HIGH' && !t.completed && (
                        <Badge kind={PRIORITY_MAP.HIGH.kind}>عاجلة</Badge>
                      )}

                      {t.subtaskCount > 0 && (
                        <span className="tiny mute num" title="المهام الفرعية">
                          <Icon name="listUl" size={11} /> {t.subtaskDone}/{t.subtaskCount}
                        </span>
                      )}
                      {t.commentCount > 0 && (
                        <span className="tiny mute num" title="تعليقات">
                          <Icon name="chat" size={11} /> {t.commentCount}
                        </span>
                      )}
                      {t.fileCount > 0 && (
                        <span className="tiny mute num" title="ملفات">
                          <Icon name="paperclip" size={11} /> {t.fileCount}
                        </span>
                      )}

                      {groupBy !== 'project' && t.projectName && (
                        <span className="ag-ctx-chip">
                          <span
                            className="ag-dot"
                            style={{ background: t.projectColor ?? undefined }}
                          />
                          {t.projectName}
                        </span>
                      )}
                      {t.accountName && (
                        <span className="ag-ctx-chip">
                          <Avatar name={t.accountName} size={16} />
                          {t.accountName}
                        </span>
                      )}
                      {t.dueAt && (
                        <span
                          className={`tiny num${b.key === 'overdue' ? ' ag-late-text' : ' mute'}`}
                        >
                          {date(t.dueAt)}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}

      {openTask && (
        <TaskDrawer
          taskId={openTask}
          onClose={() => {
            setOpenTask(null);
            if (params.get('task')) {
              params.delete('task');
              setParams(params, { replace: true });
            }
          }}
        />
      )}
    </div>
  );
}
